import * as React from "react";
import { LoadingSkeleton } from "./LoadingSkeleton";
import { ErrorPanel } from "./ErrorPanel";
import { EmptyState } from "./EmptyState";

interface QueryLikeResult<T> {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data: T | undefined;
  refetch: () => void;
}

interface QueryStateBoundaryProps<T> {
  query: QueryLikeResult<T>;
  emptyMessage?: string;
  isEmpty?: (data: T) => boolean;
  children: (data: T) => React.ReactNode;
}

function describeError(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    const message = String((error as { message?: unknown }).message);
    if (message.toLowerCase().includes("timeout")) {
      return "The request to the backend timed out.";
    }
    if (message.toLowerCase().includes("network")) {
      return "The backend is unavailable — check that it is running.";
    }
    return message;
  }
  return "An unexpected error occurred.";
}

/**
 * Uniform loading/error/empty/success rendering for one query result.
 * Shared by every dashboard section so each one only supplies its own
 * success-state rendering.
 */
export function QueryStateBoundary<T>({
  query,
  emptyMessage = "No data reported by the backend.",
  isEmpty,
  children,
}: QueryStateBoundaryProps<T>): React.JSX.Element {
  if (query.isLoading) {
    return <LoadingSkeleton />;
  }
  if (query.isError) {
    return <ErrorPanel message={describeError(query.error)} onRetry={() => query.refetch()} />;
  }
  if (query.data === undefined || (isEmpty && isEmpty(query.data))) {
    return <EmptyState message={emptyMessage} />;
  }
  return <>{children(query.data)}</>;
}
